/**
 * Per-tab client instance identity for closed-loop AI canvas mutations.
 *
 * Each browser tab gets a stable id (sessionStorage-backed, so it survives
 * reloads but is never shared across tabs/windows). The id is sent with every
 * copilot stream request; the backend binds pending client-side canvas
 * actions to it, and only the bound tab may execute and acknowledge them.
 */

const CLIENT_INSTANCE_ID_KEY = 'streamlain:ai-client-instance-id';

let inMemoryClientInstanceId: string | null = null;

function generateClientInstanceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `ci-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function getClientInstanceId(): string {
  if (inMemoryClientInstanceId) {
    return inMemoryClientInstanceId;
  }
  try {
    const stored = sessionStorage.getItem(CLIENT_INSTANCE_ID_KEY);
    if (stored) {
      inMemoryClientInstanceId = stored;
      return stored;
    }
    const created = generateClientInstanceId();
    sessionStorage.setItem(CLIENT_INSTANCE_ID_KEY, created);
    inMemoryClientInstanceId = created;
    return created;
  } catch {
    // Session storage can be disabled; fall back to a per-page-load id.
    inMemoryClientInstanceId = generateClientInstanceId();
    return inMemoryClientInstanceId;
  }
}
